import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Box, Flex, Text, Button as CoreButton, showErrorToast, ConfirmDialog } from 'core/components';
import { Button, ButtonGroup, Spinner, Callout } from '@blueprintjs/core';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Controlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/markdown/markdown';
import 'codemirror/addon/display/placeholder';
import { Field, FormComponent, TextArea as FormTextArea } from 'core/form';
import { useFormContext } from 'core/form/FormContext';
import Markdown from 'app/components/markdown/Markdown';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';
import { CodeViewerContainer, markdownCodeStyles } from './sharedStyles';

const MAX_CHARACTER_LIMIT = 100000;

const PLACEHOLDER_TEXT = `Provide details about your network environment to help AI Advisor deliver more accurate and relevant insights.
Include information such as:
- Network design and architecture (campus, WAN, data center, cloud)
- Naming conventions for devices, interfaces, and sites
- IP addressing schema
- VLAN or VRF convention
- Device types and vendors
- Network segments and their purposes (e.g. Data Centers, WAN network, Campus network, etc.)
- Limit to 100K characters`;

const FORM_FIELDS = {
  context: { label: 'Context' },
  comment: { label: 'Comment' }
};

const HeaderContainer = styled(Flex)`
  margin-top: 0px;
  margin-bottom: 16px;
  align-items: flex-start;
  flex-shrink: 0;
`;

const FormattedPreviewContainer = styled(Box)`
  border-radius: 4px;
  padding: 24px;
  background-color: ${({ theme }) => (theme.name === 'light' ? '#FFFFFF' : 'transparent')};
  margin-bottom: 16px;
  cursor: auto;
  flex: 1;
  min-height: 150px;
  overflow-y: auto;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.body};

  ${markdownCodeStyles}
`;

const EditCodeViewerContainer = styled(CodeViewerContainer)`
  margin-bottom: 8px;
  cursor: text;

  .CodeMirror {
    font-size: 14px;
  }

  .CodeMirror-cursors {
    z-index: 10 !important;
  }

  .CodeMirror-cursor {
    border-left: 2px solid #ffffff !important;
  }

  .CodeMirror-placeholder {
    color: #8a9ba8 !important;
    font-family: monospace;
  }
`;

const CharCounter = styled(Text)`
  font-size: 12px;
  margin-bottom: 16px;
  text-align: right;
  color: ${({ isExceeded, theme }) => (isExceeded ? theme.colors.red || '#db3737' : theme.colors.gray || '#8a9ba8')};
`;

const FooterContainer = styled(Box)`
  flex-shrink: 0;
  margin-bottom: 24px;

  textarea {
    border: 1px solid ${({ theme }) => theme.colors.gray3} !important;
    box-shadow: none !important;
  }
`;

const EditContextForm = observer(({ saving, versionId, matchUrl, hasExistingRevision, initialContext }) => {
  const history = useHistory();
  const form = useFormContext();
  const editorRef = useRef(null);

  const [viewMode, setViewMode] = useState('markdown');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const draftText = form.getValue('context');
  const isExceeded = draftText.length > MAX_CHARACTER_LIMIT;
  const isRedundantEmptySave = hasExistingRevision && initialContext.trim() === '' && draftText.trim() === '';

  const handleSave = () => {
    if (isExceeded) {
      showErrorToast(`Context exceeds maximum limit of ${MAX_CHARACTER_LIMIT.toLocaleString()} characters.`);
      return;
    }
    form.submit();
  };

  const handleCancel = () => {
    if (form.dirty) {
      setShowDiscardDialog(true);
      return;
    }
    history.push(`${matchUrl}/${versionId === 'latest' ? 'latest' : `${versionId}/view`}`);
  };

  const handleDiscardConfirm = () => {
    setShowDiscardDialog(false);
    history.push(`${matchUrl}/${versionId === 'latest' ? 'latest' : `${versionId}/view`}`);
  };

  return (
    <Box
      flex={1}
      minHeight={0}
      height="100%"
      display="flex"
      flexDirection="column"
      pb={2}
      px={1}
      style={{ overflowY: 'hidden' }}
    >
      <HeaderContainer width="100%">
        <Box flex={1} mr={4} mt={3}>
          <SettingsPanelHeading level={2} mt={0} mb={3} style={{ whiteSpace: 'nowrap', lineHeight: '1' }}>
            Custom Network Context
          </SettingsPanelHeading>

          <ButtonGroup>
            <Button
              active={viewMode === 'markdown'}
              intent={viewMode === 'markdown' ? 'primary' : 'none'}
              onClick={() => setViewMode('markdown')}
            >
              Markdown
            </Button>
            <Button
              active={viewMode === 'formatted'}
              intent={viewMode === 'formatted' ? 'primary' : 'none'}
              onClick={() => setViewMode('formatted')}
            >
              Formatted
            </Button>
          </ButtonGroup>
        </Box>
      </HeaderContainer>

      {viewMode === 'markdown' ? (
        <Field name="context" showLabel={false}>
          {({ field }) => (
            <EditCodeViewerContainer onClick={() => editorRef.current?.focus()}>
              <CodeMirror
                value={field.getValue()}
                editorDidMount={(editor) => {
                  editorRef.current = editor;
                }}
                onBeforeChange={(editor, data, value) => field.onChange(value)}
                options={{
                  mode: { name: 'markdown', highlightFormatting: true },
                  lineNumbers: true,
                  lineWrapping: true,
                  theme: 'default',
                  placeholder: PLACEHOLDER_TEXT
                }}
              />
            </EditCodeViewerContainer>
          )}
        </Field>
      ) : (
        <FormattedPreviewContainer>
          <Box className="markdown-body">
            <Markdown hideCsvDownloadButton>{draftText}</Markdown>
          </Box>
        </FormattedPreviewContainer>
      )}

      <CharCounter isExceeded={isExceeded}>
        {draftText.length.toLocaleString()} / {MAX_CHARACTER_LIMIT.toLocaleString()} characters
        {isExceeded && ' (Exceeds 100K character limit)'}
      </CharCounter>

      <FooterContainer>
        <Field name="comment" large>
          <FormTextArea
            fill
            disabled={saving}
            style={{ minHeight: '80px', resize: 'vertical', marginBottom: '24px' }}
          />
        </Field>
        <Flex justifyContent="flex-end">
          <CoreButton onClick={handleCancel} disabled={saving} mr={3} style={{ minWidth: '100px' }}>
            Cancel
          </CoreButton>
          <CoreButton
            intent="primary"
            onClick={handleSave}
            disabled={saving || !form.valid || !form.dirty || isExceeded || isRedundantEmptySave}
            loading={saving}
            style={{ minWidth: '100px' }}
          >
            Save
          </CoreButton>
        </Flex>
      </FooterContainer>
      <ConfirmDialog
        isOpen={showDiscardDialog}
        title="Discard Changes"
        text="You have unsaved changes. Are you sure you want to discard them?"
        confirmButtonText="Discard"
        confirmButtonIntent="danger"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscardDialog(false)}
      />
    </Box>
  );
});

const EditContext = ({ versionId, collection, matchUrl }) => {
  const history = useHistory();

  const targetModel = collection.getVersionModel(versionId);

  const isLoading = collection.isLoadingVersion(targetModel);
  const fetchError = targetModel && !targetModel.hasContent && targetModel.error;
  const isMissingVersion = !isLoading && versionId !== 'latest' && !targetModel;

  const formModelRef = useRef(null);
  if (!formModelRef.current && !isLoading) {
    formModelRef.current = collection.forge({ context: targetModel ? targetModel.get('context') : '', comment: '' });
  }
  const formModel = formModelRef.current;

  useEffect(() => {
    if (!targetModel || targetModel.hasContent) {
      return;
    }
    targetModel.fetch().catch((err) => {
      console.error('Failed to fetch context:', err);
      showErrorToast('Failed to load Custom Network Context.');
    });
  }, [targetModel]);

  const handleSave = (values) =>
    collection.saveContext({ context: values.context, comment: values.comment }).then(() => {
      history.push(`${matchUrl}/latest`);
    });

  if (isLoading || !formModel) {
    return (
      <Flex height="100%" alignItems="center" justifyContent="center">
        <Spinner size={30} />
      </Flex>
    );
  }

  if (fetchError) {
    return (
      <Box p={4}>
        <Callout intent="danger" icon="error" title="Error Loading Context">
          Failed to load Custom Network Context. Please try refreshing the page.
        </Callout>
      </Box>
    );
  }

  if (isMissingVersion) {
    return (
      <Box p={4}>
        <Callout intent="warning" icon="warning-sign" title="Version Not Found">
          The requested Custom Network Context version could not be found.
        </Callout>
      </Box>
    );
  }

  return (
    <FormComponent fields={FORM_FIELDS} model={formModel} handleSave={handleSave}>
      <EditContextForm
        saving={collection.isRequestActive('updating')}
        versionId={versionId}
        matchUrl={matchUrl}
        hasExistingRevision={Boolean(targetModel)}
        initialContext={targetModel ? targetModel.get('context') || '' : ''}
      />
    </FormComponent>
  );
};

EditContext.propTypes = {
  versionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collection: PropTypes.object.isRequired,
  matchUrl: PropTypes.string
};

EditContext.defaultProps = {
  versionId: 'latest',
  matchUrl: ''
};

export default observer(EditContext);
